/**
 * Component: AsyncSearchSelect
 * Spec: 003-optimized-ot-list
 * 
 * Componente reutilizable de búsqueda asíncrona con debounce
 * para reemplazar SelectSearch en filtros de usuarios/sedes
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { buscarUsuarios, buscarSedes } from '../../api/common';
import PropTypes from 'prop-types';

/**
 * Select con búsqueda asíncrona optimizada
 * @param {Object} props
 * @param {string} props.type - Tipo de búsqueda: 'usuario' | 'sede'
 * @param {Function} props.onChange - Callback cuando cambia el valor
 * @param {string} props.value - Valor seleccionado actual
 * @param {string} props.label - Etiqueta del campo
 * @param {string} props.name - Nombre del campo para formularios
 * @param {string} props.placeholder - Placeholder del input
 * @param {string} props.rol - Solo para type='usuario': filtrar por rol (opcional)
 * @param {number} props.limit - Límite de resultados (default: 20)
 * @param {boolean} props.required - Campo requerido
 */
export const AsyncSearchSelect = ({
  type,
  onChange,
  value = '',
  label,
  name,
  placeholder = 'Escribe para buscar...',
  rol = null,
  limit = 20,
  required = false
}) => {
  const [searchText, setSearchText] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  /**
   * Búsqueda asíncrona con debounce
   */
  const performSearch = useCallback(async (text) => {
    if (text.length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      let results = [];
      
      console.log('type: ', type);
      if (type === 'usuario') {
        const response = await buscarUsuarios(text, rol, limit);

        console.log('🔍 Resultados de búsqueda de usuarios:', response.data.data);
        results = response.data.data || [];
      } else if (type === 'sede') {
        const response = await buscarSedes(text, limit);
        console.log('🔍 Resultados de búsqueda de sedes:', response.data.data);
        results = response.data.data || [];
      }

      setOptions(results);
      setIsOpen(true);
    } catch (error) {
      console.error('Error en búsqueda asíncrona:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [type, rol, limit]);

  /**
   * Handler del input con debounce de 300ms
   */
  const handleInputChange = useCallback((e) => {
    const text = e.target.value;
    setSearchText(text);

    // Limpiar debounce anterior
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Nuevo debounce
    debounceRef.current = setTimeout(() => {
      performSearch(text);
    }, 300);
  }, [performSearch]);

  /**
   * Seleccionar opción
   */
  const selectOption = useCallback((option) => {
    const selectedValue = option._id;
    const displayText = formatOptionLabel(option, type);
    
    setSearchText(displayText);
    setIsOpen(false);
    
    // Llamar onChange con el valor seleccionado
    if (onChange) {
      onChange({
        target: {
          name,
          value: selectedValue
        }
      });
    }
  }, [onChange, name, type]);

  /**
   * Limpiar selección
   */
  const clearSelection = useCallback(() => {
    setSearchText('');
    setOptions([]);
    setIsOpen(false);
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: ''
        }
      });
    }
  }, [onChange, name]);

  /**
   * Cerrar dropdown al hacer click fuera
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Form.Group ref={wrapperRef} className="position-relative mb-3">
      {label && <Form.Label>{label}{required && ' *'}</Form.Label>}
      
      <div className="input-group">
        <Form.Control
          type="text"
          name={name}
          value={searchText}
          onChange={handleInputChange}
          onFocus={() => options.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
        />
        
        {loading && (
          <span className="input-group-text">
            <Spinner animation="border" size="sm" />
          </span>
        )}
        
        {searchText && !loading && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={clearSelection}
            title="Limpiar"
          >
            ✕
          </button>
        )}
      </div>

      {/* Dropdown de opciones */}
      {isOpen && options.length > 0 && (
        <div 
          className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
          style={{ 
            maxHeight: '250px', 
            overflowY: 'auto', 
            zIndex: 1050 
          }}
        >
          <ul className="list-group list-group-flush">
            {options.map((option) => (
              <li
                key={option._id}
                className="list-group-item list-group-item-action"
                onClick={() => selectOption(option)}
                style={{ cursor: 'pointer' }}
              >
                {formatOptionLabel(option, type)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mensaje si no hay resultados */}
      {isOpen && searchText.length >= 3 && options.length === 0 && !loading && (
        <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-2 text-muted">
          No se encontraron resultados
        </div>
      )}

      {/* Hint de caracteres mínimos */}
      {searchText.length > 0 && searchText.length < 3 && (
        <Form.Text className="text-muted">
          Escribe al menos 3 caracteres para buscar
        </Form.Text>
      )}
    </Form.Group>
  );
};

/**
 * Formatear label de opciones según tipo
 */
const formatOptionLabel = (option, type) => {
  if (type === 'usuario') {
    return `${option.nombre || ''} ${option.apellido || ''} ${option.ciudad ? `(${option.ciudad})` : ''}`.trim();
  } else if (type === 'sede') {
    return option.sede || option.nombre || 'Sin nombre';
  }
  return option.nombre || option._id;
};

AsyncSearchSelect.propTypes = {
  type: PropTypes.oneOf(['usuario', 'sede']).isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  rol: PropTypes.string,
  limit: PropTypes.number,
  required: PropTypes.bool
};

export default AsyncSearchSelect;
